<template>
  <div class="bookMain"
      :style="bookMainStyles"
    >
    <GlobalEvents @keyup.space="toggleAudio()"/>
    <div class="bookContainer" :style="bookContainerStyles">
      <div v-for="(page, index) in pages"
          :key="page.url"
          class="bookItem"
          ref="bookItem"
          @click="turn(index)"
          v-touch:swipe.left="swipeLeft"
          v-touch:swipe.right="swipeRight"
          :class="[
            {rotate180 : page.rotate180},
            page.left ? 'left' : 'right',
            page.animateClass
          ]"
          :style="{
            zIndex : set_zIndex(index),
            animationDuration: page.animationDuration + 's'
          }">
        <slot :page="page"
              :index="index">
        </slot>
        <i v-if="showPageIndex && startPageIndex <= index && endPageIndex >= index"
          class="index"
          :class="index % 2 === 0 ? 'index-left' : 'index-right'"
          :style="styleForPageIndex">{{ index + 1 - startPageIndex }}
        </i>
      </div>
    </div>
    <book-navigation
      :atFirstPage=atFirstPage
      :atEndPage=atEndPage
      :hasAudioSource=!!audioSource
      :audioPlaying=audioPlaying
      :color=color
      :width=width
      :height=height
      @turnLeftPage="turnLeftPage"
      @turnRightPage="turnRightPage"
      @goToHomePage="goToHomePage"
      @toggleAudio="toggleAudio"
    />
    <template v-if="pages.length === 0">
      <div class="bookItem left">
        <loading-svg class="loading"/>
      </div>
      <div class="bookItem right">
        <loading-svg class="loading"/>
      </div>
    </template>
  </div>
</template>

<script>
  import { Howl } from 'howler'
  import GlobalEvents from 'vue-global-events'
  import bookNavigation from '../bookNavigation/bookNavigation.vue'
  import loadingSvg from '../icons/loading.vue'
  const TURNTOLEFT = 'left'
  const TURNTORIGHT = 'right'
  const HIDDEN = 'hidden'
  const VISIBLE = 'visible'
  export default {
    name: 'book',
    components: {
      GlobalEvents,
      bookNavigation,
      loadingSvg,
    },
    props: {
      styleForPageMain: {
        type: Object,
        default () {
          return {}
        }
      },
      styleForPageIndex: {
        type: Object,
        default () {
          return {}
        }
      },
      // Page data
      data: {
        type: Array,
        required: true
      },
      // Whether to allow manual page turning
      turnPageByHand: {
        type: Boolean,
        default: true
      },
      // Start page
      initPage: {
        type: Number,
        default: 1
      },
      // Whether to turn pages automatically
      autoNextPage: {
        type: Boolean,
        default: false
      },
      // Time per page
      autoNextPageDelayTime: {
        type: Number,
        default: 3000
      },
      // Page turning animation time
      duration: {
        type: Number,
        default: 1000
      },
      // Show the page number
      showPageIndex: {
        type: Boolean,
        default: true
      },
      // Start index
      startPageIndex: {
        type: Number,
        default: 0
      },
      // End of page index
      endPageIndex: {
        type: Number,
        default: 9999
      },
      color: {
        type: String,
        default: "#888"
      },
      width: {
        type: Number,
        default: 1536
      },
      height: {
        type: Number,
        default: 1241
      },
    },
    data () {
      return {
        pages: [],
        curPage: this.initPage, // The current page number, each page is two and a half pages, counting from 1
        direction: TURNTOLEFT, // Page turning direction, not button direction
        curHalfPage: 0, // Each half page, each page is equal to two half pages, counting from 0
        animating: false, // Animated state is in progress to prevent clicking to turn pages
        staying: false, // The stay status of each page,
        atFirstPage: true, // Whether we're at the first page
        atEndPage: false, // Whether we've reached the last page,
        currentTime: 0, // The position (in seconds) in the current audio
        audio: false, // The howl audio instance for the current page
        audioLoaded: false, // Whether the current page audio is loaded
      }
    },
    computed: {
      // total pages
      pageCount () {
        return Math.ceil(this.pages.length / 2)
      },
      leftPage () {
        let leftPage = this.curPage * 2 - 1 - 1
        return leftPage
      },
      rightPage () {
        let leftPage = this.curPage * 2 - 1 - 1
        let rightPage = this.data[leftPage + 1] ? leftPage + 1 : undefined
        return rightPage
      },
      audioPlaying () {
        return this.audio && this.audio.playing()
      },
      // Return the source (location) of the audio
      audioSource () {
        return this.pages[this.curHalfPage] && this.pages[this.curHalfPage].audio
      },
      bookMainStyles () {
        return Object.assign(this.styleForPageMain, {
          /*  Centre the book vertically in the viewport */
          // margin: `calc((100vh - 90vw * (${this.height} / ${this.width} / 2)) / 2) auto 0`
          margin: '0'
        })
      },
      bookContainerStyles () {
        const landscape = this.width > this.height

        if (landscape) {
          // landscape page
          return {
            border: `2px solid ${this.color}`,
            width: 'auto',
            height: `calc(90vw * (${this.height} / ${this.width} / 2))`,
            'max-height': `calc(1600px * (${this.height} / ${this.width} / 2))`,
            margin: 'auto',
          }
        }

        // portrait page
        return {
          border: `2px solid ${this.color}`,
          width: `calc(100vh * (${this.height} / ${this.width}))`,
          height: 'calc(100vh - 60px)',
          'max-height': `calc(1600px * (${this.height} / ${this.width} / 2))`,
          margin: 'auto',
        }
      },
    },
    methods: {
      assign (obj, target) {
        for (let k in target) {
          if (target.hasOwnProperty(k)) {
            obj[k] = target[k]
          }
        }
        return obj
      },
      $$emit (type) {
        let leftPage = this.curPage * 2 - 1 - 1
        let rightPage = this.data[leftPage + 1] ? leftPage + 1 : undefined
        this.$emit(type, this.curPage, [leftPage, rightPage], [this.data[leftPage], this.data[rightPage]])
      },
      // Process all pages
      getPages () {
        // Initialization data
        const pages = []
        this.data.forEach((item, index) => {
          let page = this.assign({
            animateClass: '',
            rotate180: false,
            animationDuration: '0s',
            left: false,
            _left: false
          }, item)
          // Everything before the current page is on the left
          if (index <= this.curPage * 2 - 1 - 1) {
            page.left = true
            page._left = true
          }
          pages.push(page)
        })
        this.pages = pages
        if (this.autoNextPage) {
          this.stay(this.curHalfPage)
        }
      },
      // Get the current page number by index
      getPageNumByIndex (index) {
        return Math.ceil((index + 1) / 2)
      },
      // Turn the page towards the left
      turnLeftPage () {
        this.turn(this.leftPage)
      },
      // Turn the page towards the right
      turnRightPage () {
        this.turn(this.rightPage)
      },
      // Navigate to the home page
      goToHomePage () {
        window.document.location.href = window.document.location.href;
        // TODO: figure out why the following doesn't work correctly
        // this.prev(this.curPage - 1)
        // this.resetAudio()
        // this.resetAnimateClass()
        // this.direction = TURNTOLEFT
        // this.animating = false
      },
      // Handle swipes to the left
      swipeLeft() {
        this.turnRightPage()
        this.$emit('swipeLeft')
      },
      // Handle swipes to the right
      swipeRight() {
        this.turnLeftPage()
        this.$emit('swipeRight')
      },
      step () {
        // Determine our current seek position.
        if (this.audio) {
          const seek = this.audio.seek() || 0;
          this.currentTime = Math.floor(seek)
        }

        // timer.innerHTML = self.formatTime(Math.floor(seek));
        // progress.style.width = (((seek / this.audio.duration()) * 100) || 0) + '%';

        // If the sound is still playing, continue stepping.
        if (this.audioPlaying) {
          requestAnimationFrame(this.step.bind(this))
        }
      },
      // Toggle the playing of the audio
      toggleAudio () {
        // Do we need to load the audio (because we've changed the page)?
        if (this.currentTime === 0) {
          this.audioLoaded = false
          this.audio = new Howl({
            src: [this.audioSource],
            onplay: () => {
              requestAnimationFrame(this.step.bind(this))
            },
            onend: this.handleAudioEnded,
            onload: this.handleAudioLoaded,
          })
        }

        if (!this.audioPlaying && this.audioSource) {
          this.audio.play()
          this.audio.currentTime = this.currentTime
        } else {
          this.audio.pause()
        }
      },
      // Reset the playing of the audio
      resetAudio () {
        if (this.audioPlaying) {
          this.audio.pause()
        }
        this.currentTime = 0
        this.audio = false
      },
      // Handle the event when the audio has completed playing
      handleAudioEnded () {
        this.currentTime = 0
        this.$emit('audioEnded')
      },
      handleAudioLoaded() {
        this.audioLoaded = true
        this.$emit('audioLoaded')
      },
      // When turning the page to the right, add the flip class name to the even-numbered pages
      // (not including the first page), and when turning the page to the left,
      // add the flip class name to the odd-numbered pages (not including the last page).
      rotate180 () {
        this.pages.forEach((page, index) => {
          if (this.direction === TURNTOLEFT) {
            page.rotate180 = index > 1 && index % 2 === 0
          } else if (this.direction === TURNTORIGHT) {
            page.rotate180 = index < this.pages.length - 1 && index % 2 === 1
          }
        })
      },
      // Add the animation class name, and the animation will be performed immediately after adding
      animateClass () {
        // The end event will be executed twice on the front and back of the book page,
        // and the restriction here is only allowed to trigger once
        let emitTurnEnd = true
        this.pages.forEach((page, index) => {
          page.animationDuration = this.duration / 1000
          if (this.direction === TURNTOLEFT) {
            // Do not add the first page
            if (this.curPage === 1) return
            // After the animation is complete, keep it in the position after the animation
            let time = setTimeout(() => {
              page._left = true
              this.animating = false
              if (emitTurnEnd) {
                emitTurnEnd = false
                this.$$emit('turnEnd')
              }
            }, this.duration + 50)
            // Turn over the previous page in the animation and hide it
            if (this.curPage * 2 - 3 === index) {
              page.animateClass = `${TURNTOLEFT}-${HIDDEN}`
            } else if (this.curPage * 2 - 2 === index) {
              // Turn over the next page in the animation to display
              page.animateClass = `${TURNTOLEFT}-${VISIBLE}`
            } else {
              // No animation, clear timer
              clearTimeout(time)
              // Do not add class name
              page.animateClass = ''
            }
          } else if (this.direction === TURNTORIGHT) {
            // Do not add the last page
            if (this.curPage === this.pageCount) return
            // After the animation is complete, keep it in the position after the animation
            let time = setTimeout(() => {
              page._left = false
              this.animating = false
              if (emitTurnEnd) {
                emitTurnEnd = false
                this.$$emit('turnEnd')
              }
            }, this.duration + 50)
            // Turn over the previous page in the animation and hide it
            if (this.curPage * 2 - 1 === index) {
              page.animateClass = `${TURNTORIGHT}-${VISIBLE}`
            } else if (this.curPage * 2 === index) {
              // Turn over the next page in the animation to display
              page.animateClass = `${TURNTORIGHT}-${HIDDEN}`
            } else {
              // No animation, so clear timer
              clearTimeout(time)
              // Do not add class name
              page.animateClass = ''
            }
          }
        })
      },
      // Reset the animation class name to empty
      resetAnimateClass () {
        this.pages.forEach(page => {
          page.animateClass = ''
          page.left = page._left
        })
      },
      // Set the stacking height of book pages, the current page is the highest,
      // while other pages are decreasing
      set_zIndex (index) {
        var pageNum = this.getPageNumByIndex(index)
        if (this.curPage === pageNum) {
          // The current page takes the total number of pages as the level value
          return this.pageCount
        } else {
          // The stacking height on both sides of the current page decreases sequentially
          return Math.abs(this.pageCount - Math.abs(this.curPage - pageNum))
        }
      },
      // Turn the page
      turn (index) {
        if (!this.turnPageByHand) return
        if (this.animating) return
        this.resetAudio()
        this.resetAnimateClass()
        if (index % 2 === 0) {
          this.prev()
        } else {
          this.next()
        }
      },
      // Go to the previous page
      prev (num) {
        num = this.roundNum(num)
        if (isNaN(num) || num < 1) return
        if (this.animating) return
        if (this.curPage - num < 1) return
        // Change direction
        this.direction = TURNTORIGHT
        this.$$emit('turnStart')
        this.curPage -= num
        this.$$emit('prev')
      },
      // Go to the next page
      next (num) {
        num = this.roundNum(num)
        if (isNaN(num) || num < 1) return
        if (this.animating) return
        if (this.curPage + num > this.pageCount) return
        // Change direction
        this.direction = TURNTOLEFT
        this.$$emit('turnStart')
        this.curPage += num
        this.$$emit('next')
      },
      // Number rounding
      roundNum (any) {
        return Math.round(any || 1)
      },
      // Stay on the current page
      stay (index) {
        this.curHalfPage = index
        this.staying = true
        // Record the current page.
        // If the current page is changed by clicking to turn the page before the timer ends,
        // then the following end operations are not performed
        const temp = this.curHalfPage
        setTimeout(() => {
          if (temp === this.curHalfPage) {
            this.ended()
          }
        }, this.autoNextPageDelayTime)
      },
      // Single page preview completed
      ended () {
        this.staying = false
        // right
        if (this.curHalfPage % 2 === 1) {
          if (this.autoNextPage) {
            this.next()
          }
        } else {
          if (this.autoNextPage) {
            this.curHalfPage += 1
            this.stay(this.curHalfPage)
          }
        }
      },
      // Calculate after page changes
      computedData (pageNum) {
        // Each time the page is turned, the index of the left side of the page minus one unit
        // is played (because each half page starts with index 0, and the page starts with 1)
        this.curHalfPage = pageNum * 2 - 1 - 1
        // Turn pages for animation status
        this.animating = true
        // Calculate which pages need to be flipped 180 degrees
        this.rotate180()
        // Calculate the pages that need to be animated
        this.animateClass()
        // Stay on the current page
        this.stay(this.curHalfPage)
      },
      // Change the status of automatic page turning
      changeAutoNextPage () {
        this.autoNextPage = !this.autoNextPage
        // When setting automatic page turning, if it is not staying and
        // not in the process of turning pages, then turn to the next page
        if (this.autoNextPage && !this.staying && !this.animating) {
          this.next()
        }
      }
    },
    mounted () {
      this.getPages()
      window.addEventListener("keydown", e => {
        switch (e.code) {
          case 'ArrowLeft':
            this.turnLeftPage()
            break;
          case 'ArrowRight':
            this.turnRightPage()
            break;

          default:
            break;
        }
      });
    },
    watch: {
      // Monitor changes and re-render
      data () {
        this.getPages()
      },
      // Animation processing after page number change
      curPage (pageNum) {
        if (pageNum === 1) {
          this.atFirstPage = true
          this.atEndPage = false
          this.$$emit('atFirstPage')
        } else if (pageNum === this.pageCount) {
          this.atFirstPage = false
          this.atEndPage = true
          this.$$emit('atEndPage')
        } else {
          this.atFirstPage = false
          this.atEndPage = false
        }
        this.computedData(pageNum)
      },
      // Change every half page
      curHalfPage (num) {
        this.$emit('indexPageChange', num)
      }
    }
  }
</script>

<style scoped>
  /* Book styles */
  .bookMain{
    /* width: 90vw; */
    width: 100vw;
    max-width: 1600px;

    /*  Centre the book vertically in the viewport */
    /* margin: calc((100vh - 90vw * (1241 / 1536 / 2)) / 2) auto 0; */
  }
  .bookContainer{
    /* height: calc(90vw * (1241 / 1536 / 2)); */
    /* max-height: calc(1600px * (1241 / 1536 / 2)); */
    /* border: 2px solid #888; */
    box-sizing: border-box;

    position: relative;
    perspective:1500px;
    cursor: pointer;
    z-index: 2;
  }
  .bookMain .bookItem{
    width: 50%;
    height: 100%;
    background: #fff;
    overflow: hidden;
    position: absolute;
    top: 0;
    right: 0;
  }
  .bookMain .bookItem>.loading{
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 30px;
    height: 30px;
    margin: auto;
    animation: loading linear 1s infinite;
  }
  .bookMain .bookItem .index{
    position: absolute;
    bottom: 5px;
    font-style: normal;
    width: 20px;
    height: 20px;
    line-height: 20px;
    text-align: center;
    background: rgba(18, 150, 219, 0.4);
    border-radius: 50%;
  }
  .bookMain .bookItem .index.index-left{
    left: 5px;
  }
  .bookMain .bookItem .index.index-right{
    right: 5px;
  }
  .bookMain .bookItem.left{
    left:0;
    right:initial
  }
  .bookMain .bookItem.right{
    left: initial;
    right: 0;
  }
  /* The elements that need to be animated are reversed at the beginning,
   * and the direction changes after the animation is complete
   */
  .bookMain .bookItem.rotate180.right-hidden *,
  .bookMain .bookItem.rotate180.right-visible *,
  .bookMain .bookItem.rotate180.left-hidden *,
  .bookMain .bookItem.rotate180.left-visible *{
    transform: rotateY(180deg);
  }
  /* The position of the audio button */
  .bookMain .bookItem.rotate180.right-hidden .index.index-left,
  .bookMain .bookItem.rotate180.right-visible .index.index-left,
  .bookMain .bookItem.rotate180.left-hidden .index.index-left,
  .bookMain .bookItem.rotate180.left-visible .index.index-left{
    left: initial;
    right: 5px;
  }
  /* Page position */
  .bookMain .bookItem.rotate180.right-hidden .index.index-right,
  .bookMain .bookItem.rotate180.right-visible .index.index-right,
  .bookMain .bookItem.rotate180.left-hidden .index.index-right,
  .bookMain .bookItem.rotate180.left-visible .index.index-right{
    right: initial;
    left: 5px;
  }
  /* The page level in the animation is very high */
  .bookMain .bookItem.right-hidden {
    z-index: 99!important;
    transform-origin: right;
    animation: right-hidden linear 1s 1 both;
  }
  .bookMain .bookItem.right-visible {
    z-index: 100!important;
    transform-origin: right;
    animation: right-visible linear 1s 1 both;
  }
  .bookMain .bookItem.left-hidden {
    z-index: 99!important;
    transform-origin: left;
    animation: left-hidden linear 1s 1 both;
  }
  .bookMain .bookItem.left-visible {
    z-index: 100!important;
    transform-origin: left;
    animation: left-visible linear 1s 1 both;
  }
  /* The previous page of the page moving to the right gradually hides */
  @keyframes right-hidden {
    0% { /* Animation starts */
      opacity: 1;
      transform: rotate3d(0, 1, 0, 0deg);
    }
    50% {
      opacity: 1;
      box-shadow: 0px 0px 20px #333;
      transform: rotate3d(0, 1, 0, 90deg);
    }
    50.1% {
      opacity: 0;
      transform: rotate3d(0, 1, 0, 90deg);
    }
    100% { /* Animation ends */
      opacity: 0;
      box-shadow: none;
      transform: rotate3d(0, 1, 0, 180deg);
    }
  }

  /* The next page after the page moves to the right gradually appears */
  @keyframes right-visible {
    0% { /* Animation starts */
      opacity: 0;
      transform: rotate3d(0, 1, 0, 0deg);
    }
    50% {
      opacity: 0;
      box-shadow: 0px 0px 20px #333;
      transform: rotate3d(0, 1, 0, 90deg);
    }
    50.1% {
      opacity: 1;
      transform: rotate3d(0, 1, 0, 90deg);
    }
    100% { /* Animation ends */
      opacity: 1;
      box-shadow: none;
      transform: rotate3d(0, 1, 0, 180deg);
    }
  }

  /* The previous page of the page moving to the left is gradually hidden */
  @keyframes left-hidden {
    0% { /* Animation starts */
      opacity: 1;
      transform: rotate3d(0, 1, 0, 0deg);
    }
    50% {
      opacity: 1;
      box-shadow: 0px 0px 20px #333;
      transform: rotate3d(0, 1, 0, -90deg);
    }
    50.1% {
      opacity: 0;
      transform: rotate3d(0, 1, 0, -90deg);
    }
    100% { /* Animation ends */
      opacity: 0;
      box-shadow: none;
      transform: rotate3d(0, 1, 0, -180deg);
    }
  }

  /* The next page after the page moves to the left gradually appears */
  @keyframes left-visible {
    0% { /* Animation starts */
      opacity: 0;
      transform: rotate3d(0, 1, 0, 0deg);
    }
    50% {
      opacity: 0;
      box-shadow: 0px 0px 20px #333;
      transform: rotate3d(0, 1, 0, -90deg);
    }
    50.1% {
      opacity: 1;
      transform: rotate3d(0, 1, 0, -90deg);
    }
    100% { /* Animation ends */
      opacity: 1;
      box-shadow: none;
      transform: rotate3d(0, 1, 0, -180deg);
    }
  }

  @keyframes loading {
    from{
      transform: rotateZ(0deg);
    }
    to{
      transform: rotateZ(360deg);
    }
  }
</style>



// WEBPACK FOOTER //
// book.vue?4462b90a